/**
 * The deviation couplings, as one pure function the dialog calls after every
 * change: `deviationCouplingFor(prev, next)` returns the fields the form has
 * to change in response, and nothing else (an empty patch when nothing does).
 *
 * Ported from the retired deviation dialog's effects:
 *   - the reference re-pick and the boundary clear (its three input effects);
 *   - the reference-type effect: a point-cloud design forces vertical + TIN
 *     and widens the ±5 cm / 1 cm defaults to ±1 m / 5 cm, an IFC design
 *     snaps to nearest, and `referenceMode` follows the design unless edited;
 *   - the volume / report / horizontal effects: horizontal mode measures
 *     sideways and has no vertical volume (T2), so volumes go off; a
 *     deviation-only PDF is disabled by the dialog meanwhile (not unticked).
 * The legacy station-volume couplings (ticking stations forced volume stats
 * on and the plane to XY) are gone on purpose: Volumes by chainage now come
 * from cut & fill, which has no such fields.
 */
import type { LayerData } from '@/atoms/layers.atom';
import { isLandxmlLayer, isPointcloudLayer, isVectorLayer } from '@/lib/analyses/candidates';

import { allowedDeviationModes } from './availability';
import {
  DEVIATION_PC_TOLERANCE_DEFAULT_M,
  DEVIATION_RANGE_DEFAULT_M,
  DEVIATION_TOLERANCE_DEFAULT_M,
  type CompareFormValues,
  type ComparePatch,
} from './formSchema';

/** The widened defaults for a point-cloud design: earthworks-scale change
 *  saturates a ±5 cm colour bar (legacy comment, PC↔PC). */
const PC_DESIGN_RANGE_M = 1.0;
const PC_DESIGN_TOLERANCE_M = DEVIATION_PC_TOLERANCE_DEFAULT_M;

/** The reference mode a design's type defaults to: VECTOR designs are usually
 *  several walls / surfaces; MODEL designs one TIN; a point cloud is a DSM,
 *  TIN only. Shared with `rerun.ts`, which flags a stored mode as the user's
 *  own only when it differs from this (R24). */
export const defaultReferenceMode = (design: LayerData): CompareFormValues['deviation']['referenceMode'] =>
  isVectorLayer(design) ? 'surfaces' : 'tin';

export interface CouplingInput {
  values: CompareFormValues;
  /** The A/B pool in display order (the dialog's `candidateLayers`). */
  candidates: readonly LayerData[];
  /** Every project layer: the design lookup and the boundary candidates. */
  layers: readonly LayerData[];
  /** react-hook-form dirty flags of the fields whose defaults get widened. */
  dirty?: { min?: boolean; max?: boolean; volumeTolerance?: boolean };
}

/**
 * The patch that keeps the deviation settings consistent with `next`.
 * `prev` is the state before the change, or `null` on mount (every rule that
 * keys on a change then applies to the initial state).
 */
export function deviationCouplingFor(prev: CouplingInput | null, next: CouplingInput): ComparePatch {
  const v = next.values;
  const inputs: Partial<CompareFormValues['inputs']> = {};
  const deviation: Partial<CompareFormValues['deviation']> = {};
  const addOns: Partial<CompareFormValues['addOns']> = {};
  const find = (id: string) => next.layers.find((l) => l.id === id) ?? next.candidates.find((l) => l.id === id);

  // 1. Re-pick the design when the as-built moved onto it — a cloud cannot be
  //    measured against itself. One LandXML file on both sides is left alone:
  //    two of its surfaces are a valid cut & fill (`sameLayer` judges it).
  let topId = v.inputs.topLayerId;
  const bottomId = v.inputs.bottomLayerId;
  const bottomMoved = !prev || prev.values.inputs.bottomLayerId !== bottomId;
  //    A design that is not a candidate at all (a re-run whose design was
  //    deleted) is re-picked the same way.
  const topMissing = !!topId && !next.candidates.some((l) => l.id === topId);
  if (topMissing || (bottomMoved && topId && topId === bottomId && !isLandxmlLayer(find(topId)))) {
    topId = next.candidates.find((l) => l.id !== bottomId)?.id ?? '';
    inputs.topLayerId = topId;
  }

  // 2. The boundary is a VECTOR layer that is neither input.
  const boundaryId = v.inputs.boundaryLayerId;
  if (
    boundaryId &&
    (boundaryId === topId ||
      boundaryId === bottomId ||
      !next.layers.some((l) => l.id === boundaryId && isVectorLayer(l)))
  ) {
    inputs.boundaryLayerId = '';
  }

  // 3a. What the design's type allows, ALWAYS (R25) — also on a re-run's
  //     seeded mount, whose restored values may predate the rule: a mode the
  //     design allows, and TIN only for a point cloud (a DSM reference).
  let mode = v.deviation.mode;
  const design = topId ? find(topId) : undefined;
  if (design) {
    const allowed = allowedDeviationModes(design);
    if (!allowed.includes(mode)) {
      mode = allowed[0];
      deviation.mode = mode;
    }
    if (isPointcloudLayer(design)) {
      if (v.deviation.referenceMode !== 'tin') deviation.referenceMode = 'tin';
      if (v.deviation.referenceModeEdited) deviation.referenceModeEdited = false;
    }
  }

  // 3b. A NEW design (or a fresh mount) re-defaults what depends on its type.
  if (design && (!prev || prev.values.inputs.topLayerId !== topId)) {
    if (isPointcloudLayer(design)) {
      // Earthworks-scale defaults unless the user already moved off the
      // ±5 cm / 1 cm ones.
      const dirty = next.dirty ?? {};
      if (!dirty.min && v.deviation.min === -DEVIATION_RANGE_DEFAULT_M) deviation.min = -PC_DESIGN_RANGE_M;
      if (!dirty.max && v.deviation.max === DEVIATION_RANGE_DEFAULT_M) deviation.max = PC_DESIGN_RANGE_M;
      if (!dirty.volumeTolerance && v.deviation.volumeTolerance === DEVIATION_TOLERANCE_DEFAULT_M)
        deviation.volumeTolerance = PC_DESIGN_TOLERANCE_M;
    } else if (!v.deviation.referenceModeEdited) {
      const referenceMode = defaultReferenceMode(design);
      if (v.deviation.referenceMode !== referenceMode) deviation.referenceMode = referenceMode;
    }
  }

  // 4. Horizontal mode has no vertical volume; leaving it brings volumes back.
  let volumeStats = v.deviation.volumeStats;
  if (mode === 'horizontal' && volumeStats) volumeStats = false;
  else if (prev && prev.values.deviation.mode === 'horizontal' && mode !== 'horizontal' && !volumeStats)
    volumeStats = true;
  if (volumeStats !== v.deviation.volumeStats) deviation.volumeStats = volumeStats;

  // …and a deviation-only PDF needs those volumes (its tables read them).
  // Horizontal mode is the exception: there the dialog shows the PDF
  // disabled with its own reason and strips it at submit, so the tick
  // survives and comes back by itself when the user leaves horizontal (C2).
  const deviationOnly = v.results.length === 1 && v.results[0] === 'deviation';
  if (deviationOnly && !volumeStats && v.addOns.pdf && mode !== 'horizontal') addOns.pdf = false;

  const patch: ComparePatch = {};
  if (Object.keys(inputs).length) patch.inputs = inputs;
  if (Object.keys(deviation).length) patch.deviation = deviation;
  if (Object.keys(addOns).length) patch.addOns = addOns;
  return patch;
}
